// Glubi design system, ported from the prototype (prototype/src/ds) with types. Styles stay token-based.
import { useLayoutEffect, useRef, useState, type ChangeEvent, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import {
  Sun, MessageSquare, Calculator, ChefHat, Inbox, Download, CircleHelp, FlaskConical,
  CircleCheck, CircleAlert, Clock, ArrowUp, ChevronDown, ChevronLeft, ChevronUp, Package, Undo2,
  Camera, X, FileText, Plug, Keyboard, Receipt, Check, ArrowRight, History, Mic, Plus, Signal, Menu, ShieldCheck, KeyRound, Scale, BookOpen, Users, Lock,
  LogOut, Link2, RefreshCw, Upload, Pause, Play, Trash2, Copy, WifiOff, Pencil, Eye, EyeOff, Bot, Workflow, SquareKanban, Search, List, Tags,
} from 'lucide-react';

const ICONS = {
  sun: Sun, 'message-square': MessageSquare, calculator: Calculator, 'chef-hat': ChefHat,
  inbox: Inbox, download: Download, 'help-circle': CircleHelp, 'flask-conical': FlaskConical,
  'check-circle': CircleCheck, 'alert-circle': CircleAlert, clock: Clock, 'arrow-up': ArrowUp,
  'chevron-down': ChevronDown, 'chevron-left': ChevronLeft, 'chevron-up': ChevronUp, package: Package, undo: Undo2,
  camera: Camera, x: X, 'file-text': FileText, plug: Plug, keyboard: Keyboard, receipt: Receipt,
  check: Check, 'arrow-right': ArrowRight, history: History, mic: Mic, plus: Plus, signal: Signal, menu: Menu, shield: ShieldCheck, key: KeyRound, scale: Scale, book: BookOpen, users: Users, lock: Lock,
  'log-out': LogOut, link: Link2, refresh: RefreshCw, upload: Upload, pause: Pause, play: Play, trash: Trash2, copy: Copy, 'wifi-off': WifiOff, pencil: Pencil, eye: Eye, 'eye-off': EyeOff,
  bot: Bot, workflow: Workflow, tablero: SquareKanban, search: Search, list: List, tags: Tags,
} as const;
export type NombreIcono = keyof typeof ICONS;

export function Icon({ name, size = 16, color = 'currentColor', style }: { name: NombreIcono; size?: number; color?: string; style?: CSSProperties }) {
  const C = ICONS[name];
  return (
    <span style={{ display: 'inline-flex', width: size, height: size, color, flexShrink: 0, lineHeight: 0, ...style }}>
      {C && <C size={size} strokeWidth={2} />}
    </span>
  );
}

const BUTTON_SIZES: Record<'sm' | 'md', CSSProperties> = {
  sm: { padding: '6px 12px', fontSize: 14, height: 32 },
  md: { padding: '10px 16px', fontSize: 14, height: 40 },
};

export function Button({ variant = 'primary', size = 'md', children, disabled, onClick, type = 'button', style, icon }: {
  variant?: 'primary' | 'secondary' | 'ghost'; size?: 'sm' | 'md'; children?: ReactNode; disabled?: boolean; onClick?: () => void;
  type?: 'button' | 'submit'; style?: CSSProperties; icon?: NombreIcono;
}) {
  const [hover, setHover] = useState(false);
  const s = BUTTON_SIZES[size] || BUTTON_SIZES.md;
  const base: CSSProperties = {
    fontFamily: 'var(--font-body)', fontWeight: 500, fontSize: s.fontSize, height: s.height, padding: s.padding,
    borderRadius: 'var(--radius-sm)', border: '1px solid transparent', cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.45 : 1, display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap',
    transition: 'background-color .12s ease,border-color .12s ease', ...style,
  };
  const variants: Record<string, CSSProperties> = {
    primary: { background: 'var(--accent)', color: 'var(--ink-text)', borderColor: 'var(--accent)' },
    secondary: { background: 'var(--surface-card)', color: 'var(--brand)', borderColor: 'var(--brand)' },
    ghost: { background: 'transparent', color: 'var(--brand)', borderColor: 'transparent' },
  };
  const hoverBg: Record<string, string> = { primary: '#C97812', secondary: 'var(--surface-sunken)', ghost: 'var(--surface-sunken)' };
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      style={{ ...base, ...variants[variant], ...(hover && !disabled ? { background: hoverBg[variant] } : {}) }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {icon && <Icon name={icon} size={16} />}
      {children}
    </button>
  );
}

export type EstadoBadge = 'ok' | 'error' | 'pending' | 'neutral';
const BADGE: Record<EstadoBadge, { bg: string; fg: string; icon: NombreIcono | null; label: string }> = {
  ok: { bg: '#E6F1EA', fg: 'var(--state-ok)', icon: 'check-circle', label: 'Cuadra' },
  error: { bg: '#F7E6E4', fg: 'var(--state-error)', icon: 'alert-circle', label: 'No cuadra' },
  pending: { bg: '#F3E8D3', fg: 'var(--state-pending)', icon: 'clock', label: 'Pendiente' },
  neutral: { bg: 'var(--surface-sunken)', fg: 'var(--text-secondary)', icon: null, label: '' },
};

export function Badge({ state = 'neutral', children }: { state?: EstadoBadge; children?: ReactNode }) {
  const m = BADGE[state] || BADGE.neutral;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, background: m.bg, color: m.fg, whiteSpace: 'nowrap',
      fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, padding: '4px 10px', borderRadius: 'var(--radius-sm)',
    }}>
      {m.icon && <Icon name={m.icon} size={14} color={m.fg} />}
      {children || m.label}
    </span>
  );
}

export function Tag({ children, tone = 'neutral' }: { children?: ReactNode; tone?: 'neutral' | 'amber' }) {
  const tones = {
    neutral: { bg: 'var(--surface-sunken)', fg: 'var(--text-secondary)' },
    amber: { bg: '#F3E8D3', fg: 'var(--amber-text)' },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <span style={{
      display: 'inline-block', background: t.bg, color: t.fg, fontFamily: 'var(--font-body)', fontSize: 12,
      fontWeight: 500, letterSpacing: '0.02em', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 'var(--radius-sm)',
    }}>{children}</span>
  );
}

export function AgentMessage({ from = 'constructor', children }: { from?: 'constructor' | 'negocio'; children?: ReactNode }) {
  const delConstructor = from === 'constructor';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: delConstructor ? 'flex-start' : 'flex-end', gap: 4, fontFamily: 'var(--font-body)' }}>
      {delConstructor && <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-mid)', fontFamily: 'var(--font-display)' }}>El constructor</span>}
      <div style={{
        maxWidth: 440, background: delConstructor ? 'var(--surface-card)' : 'var(--brand)', color: delConstructor ? 'var(--text-primary)' : 'var(--paper)',
        border: delConstructor ? '1px solid var(--border-default)' : 'none', borderRadius: 'var(--radius-md)', padding: '12px 16px', fontSize: 15, lineHeight: '22px',
        whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
      }}>{children}</div>
    </div>
  );
}

// On a touch screen there is no Shift: Enter breaks the line there and the button sends.
export const pantallaTactil = () => typeof window !== 'undefined' && Boolean(window.matchMedia?.('(pointer: coarse)').matches);

// Multiline: Enter sends, Shift+Enter breaks the line. It grows with the text up to a cap and then scrolls, so a long
// message stays visible as it is written and the caret is never sent back to the start of a single-line input.
export function ChatComposer({ value, onChange, onSend, placeholder = 'Describir el problema del negocio...', disabled, sendDisabled, minRows = 3, maxHeight = 280 }: { value: string; onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void; onSend?: () => void; placeholder?: string; disabled?: boolean; sendDisabled?: boolean; minRows?: number; maxHeight?: number }) {
  // `sendDisabled` blocks only sending: the next message can be written while the previous one is answered.
  const bloqueado = disabled || sendDisabled;
  const area = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const t = area.current;
    if (!t) return;
    t.style.height = 'auto';
    t.style.height = `${Math.min(t.scrollHeight, maxHeight)}px`;
    t.style.overflowY = t.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [value, maxHeight]);
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (!bloqueado) onSend?.(); }}
      style={{
        display: 'flex', alignItems: 'flex-end', gap: 10, background: 'var(--surface-card)', border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-lg)', padding: '10px 8px 8px 16px', fontFamily: 'var(--font-body)',
      }}
    >
      <textarea
        ref={area}
        value={value}
        onChange={onChange}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && !pantallaTactil()) { e.preventDefault(); if (!bloqueado) onSend?.(); }
        }}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={placeholder}
        rows={minRows}
        style={{
          flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontSize: 15, lineHeight: '22px', color: 'var(--text-primary)',
          fontFamily: 'var(--font-body)', resize: 'none', padding: 0, margin: 0, minHeight: minRows * 22, maxHeight,
        }}
      />
      <button
        type="submit"
        aria-label="Enviar"
        title="Enviar (Enter). Shift+Enter para otra línea"
        disabled={bloqueado}
        style={{
          width: 36, height: 36, borderRadius: 'var(--radius-sm)', border: 'none', background: 'var(--accent)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', cursor: bloqueado ? 'default' : 'pointer', flexShrink: 0, opacity: bloqueado ? 0.5 : 1,
        }}
      >
        <Icon name="arrow-up" size={18} color="var(--ink-text)" />
      </button>
    </form>
  );
}

export function TarjetaIndicador({ label, value, unit, trend, tone = 'neutral', onClick }: { label: ReactNode; value: ReactNode; unit?: ReactNode; trend?: ReactNode; tone?: 'neutral' | 'ok' | 'error' | 'pending'; onClick?: () => void }) {
  const tones: Record<string, string> = { neutral: 'var(--text-primary)', ok: 'var(--state-ok)', error: 'var(--state-error)', pending: 'var(--state-pending)' };
  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-card)', padding: '20px 24px', fontFamily: 'var(--font-body)', minWidth: 180, cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 500, letterSpacing: '0.02em', color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: 8 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span style={{ fontFamily: 'var(--font-display)', fontSize: 32, lineHeight: '38px', fontWeight: 600, color: tones[tone], fontVariantNumeric: 'tabular-nums' }}>{value}</span>
        {unit && <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{unit}</span>}
      </div>
      {trend && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6 }}>{trend}</div>}
    </div>
  );
}

export interface Columna<T> { key: string; label: ReactNode; numeric?: boolean; render?: (fila: T) => ReactNode }
export function Tabla<T extends object>({ columns = [], rows = [], rowKey, selectedKey, onRowClick, vacio }: {
  columns?: Columna<T>[]; rows?: T[]; rowKey?: keyof T; selectedKey?: unknown; onRowClick?: (fila: T) => void; vacio?: ReactNode;
}) {
  return (
    <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'auto', fontFamily: 'var(--font-body)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} style={{
                textAlign: c.numeric ? 'right' : 'left', fontSize: 12, fontWeight: 500, letterSpacing: '0.02em', color: 'var(--text-secondary)',
                textTransform: 'uppercase', padding: '10px 16px', borderBottom: '1px solid var(--border-default)', background: 'var(--surface-sunken)', whiteSpace: 'nowrap',
              }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => {
            const k = (rowKey ? r[rowKey] : ri) as string | number;
            const cls = [onRowClick ? 'tabla-row-click' : '', selectedKey != null && selectedKey === k ? 'tabla-row-selected' : ''].join(' ');
            return (
              <tr key={k} className={cls} onClick={onRowClick ? () => onRowClick(r) : undefined}
                style={{ borderBottom: ri < rows.length - 1 ? '1px solid var(--border-default)' : 'none' }}>
                {columns.map((c) => (
                  <td key={c.key} style={{
                    padding: '12px 16px', fontSize: 14, color: 'var(--text-primary)', textAlign: c.numeric ? 'right' : 'left',
                    fontVariantNumeric: c.numeric ? 'tabular-nums' : 'normal', fontWeight: c.numeric ? 500 : 400, whiteSpace: c.numeric ? 'nowrap' : 'normal',
                  }}>{c.render ? c.render(r) : String((r as Record<string, unknown>)[c.key] ?? '')}</td>
                ))}
              </tr>
            );
          })}
          {!rows.length && vacio && <tr><td colSpan={columns.length} style={{ padding: '18px 16px', fontSize: 14, color: 'var(--text-secondary)' }}>{vacio}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export interface FilaDesglose { key: string; etiqueta: ReactNode; detalle?: ReactNode; valor: ReactNode; nivel?: 0 | 1 }
// A calculation opened into its parts (Artículo 7): every number shows what it is made of.
export function DesgloseCalculo({ title, total, unit, rows = [], footer }: { title: ReactNode; total: ReactNode; unit?: ReactNode; rows?: FilaDesglose[]; footer?: ReactNode }) {
  return (
    <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-card)', fontFamily: 'var(--font-body)', overflow: 'hidden' }}>
      <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border-default)', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 500, color: 'var(--text-primary)' }}>{title}</div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, lineHeight: '30px', fontWeight: 600, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {total}
          {unit && <span style={{ fontSize: 14, color: 'var(--text-secondary)', marginLeft: 6, fontFamily: 'var(--font-body)', fontWeight: 400 }}>{unit}</span>}
        </div>
      </div>
      <div>
        {rows.map((r, i) => (
          <div key={r.key} style={{
            display: 'flex', justifyContent: 'space-between', gap: 12, padding: r.nivel ? '7px 24px 7px 44px' : '10px 24px', fontSize: r.nivel ? 13 : 14,
            borderBottom: i < rows.length - 1 ? '1px solid var(--border-default)' : 'none', color: r.nivel ? 'var(--text-secondary)' : 'var(--text-primary)',
          }}>
            <span style={{ minWidth: 0 }}>{r.etiqueta}{r.detalle && <span style={{ color: 'var(--text-secondary)', marginLeft: 8, fontSize: 12 }}>{r.detalle}</span>}</span>
            <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: r.nivel ? 400 : 500, whiteSpace: 'nowrap' }}>{r.valor}</span>
          </div>
        ))}
      </div>
      {footer && <div style={{ padding: '10px 24px', fontSize: 12, color: 'var(--text-secondary)', background: 'var(--surface-sunken)' }}>{footer}</div>}
    </div>
  );
}

export function Input({ label, placeholder, value, onChange, type = 'text', helper, error, unit, autoFocus, inputMode }: {
  label?: string; placeholder?: string; value: string | number; onChange: (e: ChangeEvent<HTMLInputElement>) => void; type?: string; helper?: ReactNode;
  error?: ReactNode; unit?: ReactNode; autoFocus?: boolean; inputMode?: 'text' | 'decimal' | 'numeric' | 'tel' | 'email';
}) {
  const [focus, setFocus] = useState(false);
  // Password fields get an eye button to show and hide what was typed.
  const [visible, setVisible] = useState(false);
  const clave = type === 'password';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontFamily: 'var(--font-body)' }}>
      {label && <label style={{ fontSize: 12, fontWeight: 500, letterSpacing: '0.02em', color: 'var(--text-secondary)' }}>{label}</label>}
      <div style={{
        display: 'flex', alignItems: 'center', background: 'var(--surface-card)',
        border: `1px solid ${error ? 'var(--state-error)' : focus ? 'var(--brand-mid)' : 'var(--border-default)'}`,
        borderRadius: 'var(--radius-sm)', padding: '0 12px', height: 40, gap: 8,
      }}>
        <input
          type={clave && visible ? 'text' : type} placeholder={placeholder} value={value} onChange={onChange} autoFocus={autoFocus} inputMode={inputMode}
          aria-label={label || placeholder}
          onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
          style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 16, color: 'var(--text-primary)', width: '100%', minWidth: 0, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums' }}
        />
        {unit && <span style={{ fontSize: 14, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{unit}</span>}
        {clave && (
          <button
            type="button" onClick={() => setVisible((v) => !v)} onMouseDown={(e) => e.preventDefault()}
            aria-label={visible ? 'Ocultar la contraseña' : 'Mostrar la contraseña'} aria-pressed={visible} title={visible ? 'Ocultar la contraseña' : 'Mostrar la contraseña'}
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', padding: 4, margin: '0 -4px', cursor: 'pointer', color: 'var(--text-secondary)', borderRadius: 'var(--radius-sm)' }}
          >
            <Icon name={visible ? 'eye-off' : 'eye'} size={18} />
          </button>
        )}
      </div>
      {(helper || error) && <span style={{ fontSize: 12, color: error ? 'var(--state-error)' : 'var(--text-secondary)' }}>{error || helper}</span>}
    </div>
  );
}

export type OpcionSelect = string | { value: string; label: string };
export function Select({ label, value, options = [], onChange }: { label?: string; value: string; options?: OpcionSelect[]; onChange: (e: ChangeEvent<HTMLSelectElement>) => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontFamily: 'var(--font-body)' }}>
      {label && <label style={{ fontSize: 12, fontWeight: 500, letterSpacing: '0.02em', color: 'var(--text-secondary)' }}>{label}</label>}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', height: 40 }}>
        <select value={value} onChange={onChange} aria-label={label}
          style={{ appearance: 'none', border: 'none', outline: 'none', background: 'transparent', width: '100%', height: '100%', padding: '0 36px 0 12px', fontSize: 16, color: 'var(--text-primary)', fontFamily: 'var(--font-body)' }}>
          {options.map((o) => typeof o === 'string' ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <Icon name="chevron-down" size={16} color="var(--text-secondary)" style={{ position: 'absolute', right: 12, pointerEvents: 'none' }} />
      </div>
    </div>
  );
}

// Accents and case don't matter when searching: «cafe» finds «Café».
const paraBuscar = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const MAX_VISIBLES = 100;

// A select you type into: with a long list (every supplier of the business) whoever fills it writes part of the
// name and picks. The value is always one of the options, or empty until one is picked.
// With onAgregar, what was typed and is not on the list can be added from here (the supplier that arrived today).
export function SelectBuscable({ label, value, options = [], onChange, placeholder = 'Escribe para buscar', onAgregar }: {
  label?: string; value: string; options?: string[]; onChange: (valor: string) => void; placeholder?: string; onAgregar?: (texto: string) => void;
}) {
  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto] = useState('');
  const [activa, setActiva] = useState(0);
  const lista = useRef<HTMLDivElement>(null);
  const q = paraBuscar(texto.trim());
  const coinciden = q ? options.filter((o) => paraBuscar(o).includes(q)) : options;
  const visibles = coinciden.slice(0, MAX_VISIBLES);
  const elegir = (o: string) => { onChange(o); setTexto(''); setAbierto(false); };
  const nuevo = onAgregar && texto.trim() && !options.some((o) => paraBuscar(o) === q) ? texto.trim().replace(/\s+/g, ' ') : null;
  const agregar = () => { if (nuevo) { onAgregar!(nuevo); setTexto(''); setAbierto(false); } };

  useLayoutEffect(() => {
    lista.current?.querySelector<HTMLElement>(`[data-i="${activa}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [activa]);

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setAbierto(true); setActiva((i) => Math.min(i + 1, visibles.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiva((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && abierto) { e.preventDefault(); if (visibles[activa]) elegir(visibles[activa]!); else agregar(); }
    else if (e.key === 'Escape' && abierto) { e.stopPropagation(); setAbierto(false); setTexto(''); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontFamily: 'var(--font-body)', position: 'relative' }}>
      {label && <label style={{ fontSize: 12, fontWeight: 500, letterSpacing: '0.02em', color: 'var(--text-secondary)' }}>{label}</label>}
      <div style={{
        display: 'flex', alignItems: 'center', background: 'var(--surface-card)', gap: 8, padding: '0 12px', height: 40,
        border: `1px solid ${abierto ? 'var(--brand-mid)' : 'var(--border-default)'}`, borderRadius: 'var(--radius-sm)',
      }}>
        <Icon name="search" size={16} color="var(--text-secondary)" />
        <input
          role="combobox" aria-expanded={abierto} aria-autocomplete="list" aria-label={label}
          value={abierto ? texto : value} placeholder={abierto && value ? value : placeholder}
          onFocus={() => { setAbierto(true); setTexto(''); setActiva(Math.max(0, options.indexOf(value))); }}
          onBlur={() => { setAbierto(false); setTexto(''); }}
          onChange={(e) => { setTexto(e.target.value); setActiva(0); setAbierto(true); }}
          onKeyDown={onKeyDown}
          style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 16, color: 'var(--text-primary)', width: '100%', minWidth: 0, fontFamily: 'var(--font-body)' }}
        />
        <Icon name="chevron-down" size={16} color="var(--text-secondary)" />
      </div>
      {abierto && (
        <div ref={lista} role="listbox" aria-label={label}
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 30, maxHeight: 260, overflowY: 'auto',
            background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          }}>
          {!visibles.length && !nuevo && <div style={{ padding: '10px 12px', fontSize: 14, color: 'var(--text-secondary)' }}>{options.length ? `Nada coincide con «${texto.trim()}»` : 'La lista está vacía'}</div>}
          {visibles.map((o, i) => (
            <div key={o} data-i={i} role="option" aria-selected={o === value}
              // mousedown, not click: the input's blur would close the list before the click lands.
              onMouseDown={(e) => { e.preventDefault(); elegir(o); }} onMouseEnter={() => setActiva(i)}
              style={{
                padding: '10px 12px', fontSize: 15, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                background: i === activa ? 'var(--surface-sunken)' : 'transparent', color: 'var(--text-primary)', fontWeight: o === value ? 600 : 400,
              }}>
              {o}{o === value && <Icon name="check" size={16} color="var(--brand-mid)" />}
            </div>
          ))}
          {coinciden.length > visibles.length && <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-secondary)' }}>y {coinciden.length - visibles.length} más: sigue escribiendo para encontrarla</div>}
          {nuevo && (
            <div role="option" aria-selected={false} onMouseDown={(e) => { e.preventDefault(); agregar(); }}
              style={{ padding: '10px 12px', fontSize: 15, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, color: 'var(--brand)', fontWeight: 500, borderTop: visibles.length ? '1px solid var(--border-default)' : 'none' }}>
              <Icon name="plus" size={16} /> Agregar «{nuevo}» a la lista
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function Modal({ title, subtitle, onClose, children, footer, width }: { title: string; subtitle?: ReactNode; onClose: () => void; children?: ReactNode; footer?: ReactNode; width?: number }) {
  return (
    <div className="overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={title} style={width ? { maxWidth: width } : undefined}>
        <div className="modal-head">
          <div>
            <div className="h-section">{title}</div>
            {subtitle && <div className="muted" style={{ fontSize: 13, marginTop: 3 }}>{subtitle}</div>}
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="Cerrar"><Icon name="x" size={18} /></button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

export function Vacio({ titulo, children, accion }: { titulo: string; children?: ReactNode; accion?: ReactNode }) {
  return (
    <div className="card" style={{ padding: '28px 24px', textAlign: 'center' }}>
      <div className="h-card">{titulo}</div>
      {children && <div className="muted" style={{ fontSize: 14, marginTop: 6, lineHeight: '20px' }}>{children}</div>}
      {accion && <div style={{ marginTop: 14 }}>{accion}</div>}
    </div>
  );
}

export function Cargando({ texto = 'Cargando' }: { texto?: string }) {
  return <div className="muted" role="status" style={{ padding: 24, fontSize: 14, display: 'flex', alignItems: 'center', gap: 10 }}><span className="typing"><span /><span /><span /></span>{texto}</div>;
}

export function PageHead({ title, subtitle, children }: { title: ReactNode; subtitle?: ReactNode; children?: ReactNode }) {
  return (
    <div className="page-head">
      <div>
        <h1 className="h-title">{title}</h1>
        {subtitle && <div className="muted" style={{ fontSize: 14, marginTop: 4 }}>{subtitle}</div>}
      </div>
      {children && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{children}</div>}
    </div>
  );
}
